import axios from 'axios';

const trimTrailingSlash = (value) => String(value || '').trim().replace(/\/+$/, '');

const normalizeBaseUrl = (value) => {
  const trimmed = trimTrailingSlash(value);
  if (!trimmed) return '';

  return trimmed.replace(/\/api$/, '');
};

const PROD_API_URL = 'https://hudlife.itaninetworkchain.com/api';

const baseURL = normalizeBaseUrl(
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? PROD_API_URL : '')
);

const metaniBaseURL = normalizeBaseUrl(
  import.meta.env.VITE_METANI_API_URL ||
    import.meta.env.VITE_METANI_URL ||
    (import.meta.env.PROD ? 'https://metani.itaninetworkchain.com' : 'http://localhost:3050')
);

const client = axios.create({
  baseURL: baseURL || undefined,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const metaniClient = axios.create({
  baseURL: metaniBaseURL || baseURL || undefined,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const unwrap = (response) => response.data;

export const get = (url, config = {}) => client.get(url, config).then(unwrap);
export const post = (url, data = {}, config = {}) => client.post(url, data, config).then(unwrap);
export const del = (url, config = {}) => client.delete(url, config).then(unwrap);
export const metaniGet = (url, config = {}) => metaniClient.get(url, config).then(unwrap);
export const metaniPost = (url, data = {}, config = {}) => metaniClient.post(url, data, config).then(unwrap);

const api = {
  chain: {
    status: () => get('/api/chain/status'),
    connectivity: () => get('/api/chain/connectivity'),
    info: () => get('/api/chain/info'),
    latestBlock: () => get('/api/chain/latest-block'),
    blocks: (limit = 10, offset = 0) => get('/api/chain/blocks', { params: { limit, offset } }),
    transactions: (limit = 10, page = 1) => get('/api/chain/transactions', { params: { limit, page } }),
    transaction: (hash) => get(`/api/chain/tx/${encodeURIComponent(hash)}`),
    block: (heightOrHash) => get(`/api/chain/block/${encodeURIComponent(heightOrHash)}`),
  },
  exchange: {
    pools: () => get('/api/exchange/pools'),
    quote: (payload) => post('/api/exchange/quote', payload),
    swap: (payload) => post('/api/exchange/swap', payload),
  },
  defi: {
    compound: {
      status: () => get('/api/defi/compound/status'),
      opportunities: (params = {}) => get('/api/defi/compound/opportunities', { params }),
      connectors: () => get('/api/defi/compound/connectors'),
      connect: (payload) => post('/api/defi/compound/connect', payload),
      quote: (payload) => post('/api/defi/compound/quote', payload),
      createIntent: (payload) => post('/api/defi/compound/intents', payload),
    },
  },
  bridge: {
    events: (limit = 20) => get('/api/bridge/events', { params: { limit } }),
    notify: (payload) => post('/api/bridge/notify', payload),
    quote: (payload) => post('/api/bridge/quote', payload),
    transfer: (payload) => post('/api/bridge/transfer', payload),
    history: (address, limit = 20) =>
      get(`/api/bridge/history/${encodeURIComponent(address)}`, { params: { limit } }),
    custodyAddresses: () => get('/api/custody/addresses'),
  },
  profiles: {
    resolveByPseudo: (pseudo) => metaniGet(`/api/profiles/resolve/${encodeURIComponent(pseudo)}`),
    byAddress: (address) => metaniGet(`/api/profiles/address/${encodeURIComponent(address)}`),
    list: (limit = 50, offset = 0) => metaniGet('/api/profiles', { params: { limit, offset } }),
  },
  wallet: {
    balance: (address) => get(`/api/wallet/balance/${encodeURIComponent(address)}`),
    supportedChains: () => get('/api/wallet/supported-chains'),
  },
  custody: {
    addresses: () => get('/api/custody/addresses'),
  },
  faucet: {
    claim: (payload) => post('/api/faucet/claim', payload),
  },
  oracle: {
    price: () => get('/api/oracle/price'),
  },
  feedback: {
    submit: (payload) => post('/api/feedback', payload),
  },
  sync: {
    state: () => get('/api/sync/state'),
  },
  sso: {
    challenge: (payload) => metaniPost('/api/sso/challenge', payload),
    login: (payload) => metaniPost('/api/sso/login', payload),
    verify: (payload) => metaniPost('/api/sso/verify', payload),
    profile: (address) => metaniGet(`/api/sso/profile/${encodeURIComponent(address)}`),
    registerProfile: (payload) => metaniPost('/api/sso/register-profile', payload),
  },
  auth: {
    register: (payload) => metaniPost('/api/auth/register', payload),
    login: (payload) => metaniPost('/api/auth/login', payload),
    me: () => metaniGet('/api/auth/me'),
  },
};

export { client, api };
export default api;
